import { useState, useEffect, useCallback } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { PageContainer, DataTable, Button } from "components/ui";
import LeaveRequestForm from "views/Rh/LeaveRequestForm";

const LEAVE_TYPES = {
  PAID:      "Paid leave",
  SICK:      "Sick leave",
  UNPAID:    "Unpaid",
  MATERNITY: "Maternity",
  OTHER:     "Other",
};

const statusBadge = (status) => {
  const map = {
    PENDING:   { bg: "rgba(245,158,11,0.15)",  color: "#f59e0b" },
    APPROVED:  { bg: "rgba(16,185,129,0.15)",  color: "#10b981" },
    REJECTED:  { bg: "rgba(239,68,68,0.15)",   color: "#ef4444" },
    CANCELLED: { bg: "rgba(148,163,184,0.15)", color: "#94a3b8" },
  };
  const s = map[status] || map.CANCELLED;
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: "2px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
    }}>
      {status}
    </span>
  );
};

const dayCount = (start, end) => {
  if (!start || !end) return "";
  const d = (new Date(end) - new Date(start)) / 86_400_000;
  return `${d + 1}d`;
};

const MyLeaveRequests = () => {
  const axiosPrivate               = useAxiosPrivate();
  const [entries, setEntries]      = useState([]);
  const [loading, setLoading]      = useState(true);
  const [page, setPage]            = useState(0);
  const [totalPages, setTotalPages]= useState(0);
  const [formOpen, setFormOpen]    = useState(false);

  const size = 12;

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get(
        `/api/rh/leave-requests/my?page=${page}&size=${size}`
      );
      setEntries(res.data.content);
      setTotalPages(res.data.page.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const cancelRequest = async (id) => {
    try {
      await axiosPrivate.post(`/api/rh/leave-requests/${id}/cancel`);
      fetchEntries();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <PageContainer className="mt-4">
      <DataTable
        title="My leave requests"
        subtitle="Track your leave history"
        loading={loading}
        data={entries}
        rowKey={(e) => e.id}
        search={false}
        paginate={false}
        emptyTitle="No leave requests yet"
        emptySubtitle="Submit your first request using the button above."
        toolbar={
          <Button variant="primary" size="sm" onClick={() => setFormOpen(true)}>
            + New request
          </Button>
        }
        columns={[
          {
            key: "type",
            header: "Type",
            render: (e) => (
              <span style={{ fontWeight: 500 }}>{LEAVE_TYPES[e.leaveType] || e.leaveType}</span>
            ),
          },
          {
            key: "period",
            header: "Period",
            render: (e) => (
              <div>
                <span className="t-mono">{e.startDate}</span>
                <span style={{ color: "var(--text-muted)", margin: "0 6px" }}>→</span>
                <span className="t-mono">{e.endDate}</span>
                <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-muted)" }}>
                  ({dayCount(e.startDate, e.endDate)})
                </span>
              </div>
            ),
          },
          {
            key: "motif",
            header: "Reason",
            render: (e) => (
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                {e.motif || "—"}
              </span>
            ),
          },
          { key: "status", header: "Status", render: (e) => statusBadge(e.status) },
          {
            key: "review",
            header: "Review note",
            render: (e) =>
              e.reviewComment ? (
                <span style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
                  "{e.reviewComment}"
                </span>
              ) : (
                <span style={{ color: "var(--text-muted)" }}>—</span>
              ),
          },
          {
            key: "actions",
            header: "",
            align: "right",
            render: (e) =>
              e.status === "PENDING" ? (
                <Button
                  variant="ghost"
                  size="sm"
                  style={{ color: "#ef4444" }}
                  onClick={() => cancelRequest(e.id)}
                >
                  Cancel
                </Button>
              ) : null,
          },
        ]}
        footer={
          totalPages > 1 ? (
            <div className="dt-foot">
              <span>Page <strong>{page + 1}</strong> of {totalPages}</span>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button
                  variant="ghost" size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ‹ Prev
                </Button>
                <Button
                  variant="ghost" size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next ›
                </Button>
              </div>
            </div>
          ) : null
        }
      />

      <LeaveRequestForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={fetchEntries}
      />
    </PageContainer>
  );
};

export default MyLeaveRequests;